import dagre from '@dagrejs/dagre';
import { type FC, type ReactNode, useEffect, useMemo } from 'react';
import {
  type Edge,
  type Node,
  Position,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ArrowEdgeFlow, Box, theme } from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { useMinistries } from '@/features/master-table/hooks';
import {
  OFFSET_GRAPH_HEADING_HEIGHT,
  PAPER_FILE_SIZE,
  type PaymentNodeData,
  calculateAndFormatPaymentTotal,
  getMinistryName,
} from '@/features/mold/utils';
import type { IndirectCostModel } from '@/features/project/projects/_models/payment';
import type { PaymentEdgesModel } from '@/features/project/projects/_models/payment-edge';
import type { PaymentGroupsModel } from '@/features/project/projects/_models/payment-group';
import type { PaymentNodesMetaModel } from '@/features/project/projects/_models/payment-node-meta';
import { useLocaledAmount } from '@/providers';
import { BigCalculator, FALLBACK_TEXT, formatValueByUnit } from '@/utils';
import type { z } from 'zod';
import { PaymentGraphNode } from './PaymentGraphNode';

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 172;
const nodeHeight = 36;

const nodeTypes = {
  customNode: PaymentGraphNode,
};

const getLayoutedElements = (
  nodes: Array<Node<PaymentNodeData>>,
  edges: Edge[],
) => {
  dagreGraph.setGraph({ rankdir: 'TB' });

  for (const node of nodes) {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  }

  for (const edge of edges) {
    dagreGraph.setEdge(edge.source, edge.target);
  }

  dagre.layout(dagreGraph);

  for (const node of nodes) {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = Position.Top;
    node.sourcePosition = Position.Bottom;

    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };
  }

  return { nodes, edges };
};

const getEdges = ({
  paymentEdges,
  projectId,
}: {
  paymentEdges: z.infer<typeof PaymentEdgesModel>;
  projectId: string;
}) => {
  return paymentEdges.map<Edge>((paymentEdge) => ({
    id: paymentEdge.id,
    source: paymentEdge.isConnectedToSourceRoot
      ? projectId
      : (paymentEdge.sourceNodeId ?? ''),
    target: paymentEdge.targetNodeId,
    data: {
      label: paymentEdge.label,
      variant: 'secondary',
    },
  }));
};

type Props = {
  paymentGroups: z.infer<typeof PaymentGroupsModel>;
  paymentNodesMeta: z.infer<typeof PaymentNodesMetaModel>;
  paymentEdges: z.infer<typeof PaymentEdgesModel>;
  panelNode?: (nodes: Node<PaymentNodeData>[]) => ReactNode;
  projectId: string;
  ministryId: string;
  paymentIndirectCosts: z.infer<typeof IndirectCostModel> | null;
  hideBorder?: boolean;
};

export const PaymentGraphLayout: FC<Props> = ({
  paymentGroups,
  paymentNodesMeta,
  paymentEdges,
  panelNode,
  projectId,
  ministryId,
  paymentIndirectCosts,
  hideBorder,
}) => {
  const { unit } = useLocaledAmount();
  const { token } = theme.useToken();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;

  const ministries = useMinistries();
  const ministryName = getMinistryName({
    targetId: ministryId,
    ministries: ministries.data,
  });

  const defaultNodeOption = {
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    type: 'customNode',
    position: { x: 0, y: 0 },
  };
  const totalAmountOfIndirectCosts =
    paymentIndirectCosts?.costs.reduce<{
      amount: number | null;
      hasNegativeAmount: boolean;
    }>(
      (previous, current) => {
        if (current.amount === undefined || current.amount === null) {
          return previous;
        }
        return {
          amount: BigCalculator.add(previous.amount ?? 0, current.amount ?? 0),
          hasNegativeAmount: previous.hasNegativeAmount || current.amount < 0,
        };
      },
      { amount: null, hasNegativeAmount: false },
    ) ?? null;

  // 国自らが支出する間接経費
  const paymentIndirectCostTextList = useMemo<string[] | null>(() => {
    if (!paymentIndirectCosts) {
      return null;
    }

    const isTotalAmountNull = totalAmountOfIndirectCosts?.amount === null;
    const totalAmountText: string = [
      paymentIndirectCosts.label,
      paymentIndirectCosts.label && !isTotalAmountNull ? ': ' : '',
      isTotalAmountNull
        ? ''
        : // type制御のため0にフォールバック。isTotalAmountNullでガードしているので0にfallbackしても問題ない。
          formatValueByUnit(totalAmountOfIndirectCosts?.amount ?? 0, {
            operation: 'round',
            unit,
            negativeCount: totalAmountOfIndirectCosts?.hasNegativeAmount
              ? 1
              : undefined,
            // 正負相殺で合計が0になったフォールバック表示（--）には単位を付けない
            showUnitLabel: !(
              totalAmountOfIndirectCosts?.hasNegativeAmount &&
              totalAmountOfIndirectCosts.amount === 0
            ),
            isDebugMode,
          }),
    ].join('');

    const itemAmountTextList = paymentIndirectCosts.costs.map((cost, i) => {
      const indexWithDot = `${i + 1}. `;
      return [
        indexWithDot,
        cost.label,
        cost.label ? ': ' : '',
        cost.amount === null
          ? FALLBACK_TEXT
          : formatValueByUnit(cost.amount, {
              operation: 'round',
              unit,
              showUnitLabel: true,
              isDebugMode,
            }),
      ].join('');
    });

    return [totalAmountText, ...itemAmountTextList];
  }, [paymentIndirectCosts, totalAmountOfIndirectCosts, unit, isDebugMode]);

  const rootNodes: Array<Node<PaymentNodeData>> = [
    {
      ...defaultNodeOption,
      id: projectId,
      data: {
        label: calculateAndFormatPaymentTotal({
          type: 'rootNodes',
          paymentEdges,
          paymentGroups,
          unit,
          totalAmountOfIndirectCosts: totalAmountOfIndirectCosts?.amount,
          isDebugMode,
        }),
        title: ministryName,
        paymentIndirectCostTextList,
      },
    },
  ];

  const initialNodes: Array<Node<PaymentNodeData>> = paymentGroups.map<
    Node<PaymentNodeData>
  >((paymentGroup) => {
    const hasNegativeAmount = !!paymentGroup.negativeTotalAmountCount;
    return {
      ...defaultNodeOption,
      id: paymentGroup.id,
      data: {
        label:
          typeof paymentGroup.totalAmount === 'number'
            ? formatValueByUnit(paymentGroup.totalAmount, {
                operation: 'round',
                unit,
                negativeCount: hasNegativeAmount ? 1 : undefined,
                // 正負相殺で合計が0になったフォールバック表示（--）には単位を付けない
                showUnitLabel: !(
                  hasNegativeAmount && paymentGroup.totalAmount === 0
                ),
                isDebugMode,
              })
            : calculateAndFormatPaymentTotal({
                type: 'initialNodes',
                paymentEdges,
                paymentGroups,
                paymentGroup,
                unit,
                isDebugMode,
              }),
        displayCode: paymentGroup.displayCode,
        title: paymentGroup.name,
        paymentCount: paymentGroup.paymentCount,
        overview: paymentGroup.overview,
      },
    };
  });

  const initialEdges: Edge[] = getEdges({
    paymentEdges,
    projectId,
  });

  const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
    [...rootNodes, ...initialNodes],
    initialEdges,
  );
  const layoutedNodesByUser = layoutedNodes.map<Node<PaymentNodeData>>(
    (layoutedNode) => {
      const paymentNode = paymentNodesMeta.find(
        (paymentNode) => paymentNode.nodeId === layoutedNode.id,
      );
      if (!paymentNode?.position) {
        return layoutedNode;
      }
      return { ...layoutedNode, position: paymentNode.position };
    },
  );

  const [nodes, , onNodesChange] = useNodesState(layoutedNodesByUser);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  useEffect(() => {
    const updatedEdges: Edge[] = getEdges({
      paymentEdges,
      projectId,
    });

    setEdges(updatedEdges);
  }, [paymentEdges, projectId, setEdges]);

  return (
    <Box
      // To fit in A4 with RsProjectLedgerPage.
      height={`calc(${PAPER_FILE_SIZE.A4.width} - ${OFFSET_GRAPH_HEADING_HEIGHT})`}
      width={PAPER_FILE_SIZE.A4.height}
      border={hideBorder ? undefined : `1px solid ${token.colorBorder}`}
      borderRadius={hideBorder ? undefined : 8}
    >
      <ArrowEdgeFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        attributionPosition="bottom-left"
      >
        {panelNode?.(nodes)}
      </ArrowEdgeFlow>
    </Box>
  );
};
